import * as React from 'react';

import type {GetQuoteById_quote_subtotalLineItems_edges_node as QuoteSubtotalLineItemType} from 'nutshell-graphql-types';

import {Badge} from '../../badge/badge';
import {colors} from '../../colors/colors';
// @ts-expect-error - imported from .js/.jsx file
import {CloseIcon} from '../../icon/svg/close-icon';
import {StackLegacy} from '../../layout/stack/stack-legacy';
import {SelectPickerGeneric} from '../../select-picker-generic/select-picker-generic';
import {TextField} from '../../textfield/textfield';
import {Body} from '../../typography/body';

import type {FieldError} from '../helpers';
import {
    hasNumberValidationError,
    hasMaxLength,
    resetErrorMessage,
} from '../quotes-product-table/helpers';
import {OPERATION_MAP, MODIFIER_UNITS} from '../wrappers/quote-subtotal-wrapper';

import styles from './quote-subtotal-row.module.css';

const OPERATION_OPTIONS = [
    {value: '+', label: '+'},
    {value: '-', label: '-'},
];

const MAX_LABEL_LENGTH = 50;

const LABEL_FIELD = 'label';
const VALUE_FIELD = 'value';

type Props = {
    subtotal: number;
    subtotalLineItem: QuoteSubtotalLineItemType;
    onDeleteRow: (id: string) => void;
    onUpdateLineItem: (lineItem: QuoteSubtotalLineItemType) => void;
};

export function QuoteSubtotalRow(props: Props) {
    const [label, setLabel] = React.useState<string>(props.subtotalLineItem.label);
    const [operation, setOperation] = React.useState<string>(
        props.subtotalLineItem.operation === OPERATION_MAP.ADD ? '+' : '-'
    );
    const [unit, setUnit] = React.useState<string>(
        props.subtotalLineItem.modifierUnit === MODIFIER_UNITS.PERCENT
            ? '%'
            : props.subtotalLineItem.price.symbol
    );
    const [value, setValue] = React.useState<string>(getFormattedValue(props.subtotalLineItem));
    const [errorMessage, setErrorMessage] = React.useState<FieldError>({
        message: '',
        field: '',
    });

    const [totalFormatted, setTotalFormatted] = React.useState<string>(
        props.subtotalLineItem.price.formattedWithCents
    );

    function getFormattedValue(lineItem: QuoteSubtotalLineItemType) {
        return lineItem.operation === OPERATION_MAP.SUBTRACT
            ? lineItem.modifierValue.toString().replace('-', '')
            : lineItem.modifierValue.toString();
    }

    // Validate that the value is a number and has at most 2 decimal places
    const handleValueChange = (val: string) => {
        if (hasNumberValidationError(val, VALUE_FIELD, 'Value', 2, 10, setErrorMessage)) {
            return;
        }

        resetErrorMessage(setErrorMessage);

        const numericValue = val.replace(/[^0-9.]/g, '');
        const num = parseFloat(numericValue);

        if ((!isNaN(num) && num >= 0) || numericValue === '') {
            setValue(numericValue);
        }
    };

    // Cap label character length at 50
    const handleLabelChange = (newLabel: string) => {
        if (hasMaxLength(newLabel, MAX_LABEL_LENGTH)) {
            setErrorMessage({
                message: `Label cannot be longer than ${MAX_LABEL_LENGTH} characters`,
                field: LABEL_FIELD,
            });

            return;
        }

        resetErrorMessage(setErrorMessage);
        setLabel(newLabel);
    };

    const getFormattedTotal = (total: number) => {
        return `${operation === '-' ? operation : ''}${
            props.subtotalLineItem.price.symbol
        }${Math.abs(total).toFixed(2)}`;
    };

    React.useEffect(() => {
        const numericValue = value === '' ? 0 : parseFloat(value);
        // Even if the value is invalid, we should still update with a 0
        let calculatedTotal = 0;

        if (!isNaN(numericValue)) {
            if (unit === '%') {
                calculatedTotal = (props.subtotal * numericValue) / 100;
            } else {
                calculatedTotal = numericValue;
            }
            calculatedTotal = parseFloat(calculatedTotal.toFixed(2));
        }

        setTotalFormatted(getFormattedTotal(calculatedTotal));

        props.onUpdateLineItem({
            ...props.subtotalLineItem,
            modifierValue: numericValue,
            modifierUnit: unit === '%' ? MODIFIER_UNITS.PERCENT : MODIFIER_UNITS.FIXED_AMOUNT,
            label: label,
            operation: operation === '+' ? OPERATION_MAP.ADD : OPERATION_MAP.SUBTRACT,
            price: {
                ...props.subtotalLineItem.price,
                amount: calculatedTotal,
                formatted: totalFormatted,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [operation, unit, value, label, totalFormatted]);

    return (
        <div className='full-width pos-relative pad-4'>
            <div className={styles['delete-icon']}>
                <Badge
                    contents={<CloseIcon size={8} fill={colors.white} wrapWithDiv={false} />}
                    onClick={() => props.onDeleteRow(props.subtotalLineItem.id)}
                    color='grey'
                />
            </div>
            <StackLegacy spacing={8} isHorizontal={true}>
                <div className={styles['item-type']}>
                    <TextField
                        value={label}
                        onChange={(type) => handleLabelChange(type)}
                        // @ts-expect-error
                        onBlur={() => resetErrorMessage(setErrorMessage)}
                        hasError={errorMessage.field === LABEL_FIELD}
                    />
                </div>
                <div className={styles.dropdown}>
                    <SelectPickerGeneric
                        options={OPERATION_OPTIONS}
                        value={{value: operation, label: operation}}
                        // @ts-expect-error
                        onChange={(op) => setOperation(op.value)}
                        minHeight={34}
                    />
                </div>
                <div className={styles.value}>
                    <TextField
                        value={value}
                        onChange={(val) => handleValueChange(val)}
                        // @ts-expect-error
                        onBlur={() => resetErrorMessage(setErrorMessage)}
                        textAlign='center'
                        hasError={errorMessage.field === VALUE_FIELD}
                    />
                </div>
                <div className={styles.dropdown}>
                    <SelectPickerGeneric
                        options={[
                            {
                                value: props.subtotalLineItem.price.symbol,
                                label: props.subtotalLineItem.price.symbol,
                            },
                            {value: '%', label: '%'},
                        ]}
                        value={{value: unit, label: unit}}
                        // @ts-expect-error
                        onChange={(op) => setUnit(op.value)}
                        minHeight={34}
                    />
                </div>
                <div className={styles.price}>
                    <Body isBold={true}>{totalFormatted}</Body>
                </div>
            </StackLegacy>
            {errorMessage.message && (
                <Body color='error' size='small'>
                    {errorMessage.message}
                </Body>
            )}
        </div>
    );
}
